/**
 * Simple Withings authentication hooks
 * Handles OAuth flow and connection status
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { queryKeys } from '../queryKeys'

// OAuth URL construction
const WITHINGS_AUTH_URL = 'https://account.withings.com/oauth2_user/authorize2'
const REDIRECT_URI = `${window.location.origin}/settings`

/**
 * Check if user has Withings connected
 */
export function useWithingsConnection() {
  const { user } = useAuth()

  return useQuery({
    queryKey: [...queryKeys.user(user?.id || ''), 'withings', 'connection'],
    queryFn: async () => {
      if (!user) return false

      const { data } = await supabase
        .from('user_credentials')
        .select('withings_session')
        .eq('user_id', user.id)
        .single()

      return !!data?.withings_session
    },
    enabled: !!user
  })
}

/**
 * Connect to Withings - starts OAuth flow
 */
export function useConnectWithings() {
  return useMutation({
    mutationFn: async () => {
      const clientId = import.meta.env.VITE_WITHINGS_CLIENT_ID
      if (!clientId) {
        throw new Error('Withings client ID not configured')
      }

      // Generate random state for CSRF protection
      const state = crypto.randomUUID()
      sessionStorage.setItem('withings_oauth_state', state)

      // Build OAuth URL
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        state,
        scope: 'user.metrics',
        redirect_uri: REDIRECT_URI
      })

      // Open OAuth flow in same window
      window.location.href = `${WITHINGS_AUTH_URL}?${params}`
    },
    onError: (error) => {
      console.error('Failed to start Withings OAuth:', error)
      toast.error('Failed to connect to Withings')
    }
  })
}

/**
 * Handle OAuth callback - exchange code for tokens
 */
export function useWithingsCallback() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      if (!user) throw new Error('Not authenticated')

      // Verify state for CSRF protection
      const savedState = sessionStorage.getItem('withings_oauth_state')
      if (state !== savedState) {
        throw new Error('Invalid OAuth state')
      }
      sessionStorage.removeItem('withings_oauth_state')

      // Exchange code for tokens via edge function
      const { data, error } = await supabase.functions.invoke('withings-api?action=token', {
        body: { code }
      })

      if (error) {
        throw new Error(error.message || 'Failed to exchange code')
      }

      if (!data) {
        throw new Error('No response from Withings API')
      }

      return data
    },
    onSuccess: (data) => {
      // Check if edge function returned success
      if (data?.success) {
        // Revalidate Withings queries to fetch fresh data
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.user(user?.id || ''), 'withings']
        })
      } else {
        // Handle edge function errors
        throw new Error(data?.error || 'Connection failed')
      }
    },
    onError: (error) => {
      console.error('Withings callback error:', error)

      // Provide user-friendly error messages based on error type
      let userMessage = 'Failed to connect to Withings'

      if (error.message.includes('OAuth state')) {
        userMessage = 'Security validation failed. Please try connecting again.'
      } else if (error.message.includes('Not authenticated')) {
        userMessage = 'Please log in and try again.'
      } else if (error.message.includes('No response')) {
        userMessage = 'Withings service is temporarily unavailable. Please try again later.'
      } else if (error.message.includes('Failed to exchange code')) {
        userMessage = 'Authorization failed. Please try connecting again.'
      } else if (error.message) {
        userMessage = `Connection failed: ${error.message}`
      }

      toast.error(userMessage)
    }
  })
}

/**
 * Disconnect from Withings
 */
export function useDisconnectWithings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('user_credentials')
        .update({ withings_session: null })
        .eq('user_id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Disconnected from Withings')

      // Directly set connection status to false instead of invalidating
      queryClient.setQueryData([...queryKeys.user(user?.id || ''), 'withings', 'connection'], false)

      // Only invalidate weight data queries to clear weight UI
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.user(user?.id || ''), 'withings', 'weight']
      })
    },
    onError: () => {
      toast.error('Failed to disconnect from Withings')
    }
  })
}
